import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useAppState } from "Context/StateContext";
import { Order } from "Models/order";
import MembersService from "Services/members.service";
import TeamsService from "Services/teams.service";
import { createWhereConditionForBranchId } from "Util/createWhereConditionForBranchId";
import { TeamKeys } from "Util/queries";

import FleetService from "../../../../../../services/fleet.service";
import StaffService from "../../../../../../services/staff.service";
import TasksTypesService from "../../../../../../services/task.type.service";
import UserService from "../../../../../../services/user.service";
import { AvailabilityStatus, Status } from "../../../../../../util/enums";

export const useTaskActions = (order?: Order) => {
  const { currentUser } = useAppState();

  const branchId = useMemo(() => {
    return currentUser?.branches?.map((branch) => branch.id!);
  }, [currentUser?.branches]);

  const hasCompanyAndBranchIdEnabled =
    !!currentUser?.companyId && !!(order?.branchFromId || order?.toBranchId);

  const getAllLeaders = () => {
    return useQuery(
      ["leaders.getAll", currentUser?.companyId, order?.branchFromId, order?.toBranchId],
      async () => {
        const filterByBranchId: number[] = [];

        if (order?.branchFromId) filterByBranchId.push(order?.branchFromId);
        if (order?.toBranchId) filterByBranchId.push(order?.toBranchId);

        return UserService.getAll({
          attributes: JSON.stringify([
            "id",
            "name",
            "lastName",
            "fullName",
            "typeUser",
            "code",
            "email",
            "phone",
            "companyId",
            "status",
          ]),
          include: JSON.stringify([
            {
              association: "usersCompanies",
              required: true,
              where: { status: Status.ACTIVE, companyId: currentUser?.companyId, leader: 1 },
            },
            {
              association: "branches",
              required: true,
              where: filterByBranchId.length
                ? {
                    id: filterByBranchId,
                  }
                : null,
            },
          ]),
          sortBy: JSON.stringify([["name", "asc"]]),
        }).then(({ data }) => data);
      },
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        initialData() {
          return [];
        },
      }
    );
  };

  const getAllStaffs = () => {
    return useQuery({
      queryKey: ["staff.getAll", currentUser?.companyId, order?.branchFromId, order?.toBranchId],
      queryFn: async () => {
        const filterByBranch: number[] = [];

        if (order?.branchFromId) filterByBranch.push(order?.branchFromId);
        if (order?.toBranchId) filterByBranch.push(order?.toBranchId);

        return StaffService.getAll({
          status: Status.ACTIVE,
          companyId: currentUser?.companyId,
          branchId: filterByBranch.length ? filterByBranch : null,
        }).then(({ data }) => data);
      },
      initialData() {
        return [];
      },
      enabled: hasCompanyAndBranchIdEnabled,
      refetchOnWindowFocus: false,
    });
  };

  const getAllFleets = () => {
    console.log('getAllFleets')
    return useQuery({
      queryKey: ["fleet.getAll", currentUser?.companyId, branchId],
      queryFn: async () => {
        return FleetService.getAll({
          status: Status.ACTIVE,
          companyId: currentUser?.companyId,
          outOfService: 0,
          include: JSON.stringify([
            {
              association: "branches",
              required: false,
              attributes: ["id", "name"],
              where: createWhereConditionForBranchId(branchId, currentUser?.companyId),
            },
          ]),
        }).then(({ data }) => data);
      },
      initialData() {
        return [];
      },
      enabled: hasCompanyAndBranchIdEnabled,
      refetchOnWindowFocus: false,
    });
  };

  const getAllTeams = () => {
    return useQuery({
      queryKey: TeamKeys.getAll(["team.getAll", currentUser?.companyId, branchId]),
      queryFn: async () => {
        return TeamsService.getAll({
          isActive: 1,
          companyId: currentUser?.companyId,
          include: JSON.stringify([
            {
              association: "memberLeader",
            },
            {
              association: "members",
            },
            {
              association: "fleets",
            },
            {
              association: "branches",
              attributes: ["id", "name"],
              required: false,
              where: createWhereConditionForBranchId(branchId, currentUser?.companyId),
            },
          ]),
        }).then(({ data }) => data);
      },
      initialData() {
        return [];
      },
      enabled: hasCompanyAndBranchIdEnabled,
      refetchOnWindowFocus: false,
    });
  };

  const getTaskTypes = () => {
    return useQuery(
      ["order.page", "tasks.type.list", currentUser?.companyId],
      () =>
        TasksTypesService.getAll({
          status: Status.ACTIVE,
          companyId: currentUser?.companyId,
          sortBy: JSON.stringify(["sequence"]),
        }).then(({ data }) => data),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        initialData() {
          return [];
        },
      }
    );
  };

  const getAllMembers = () => {
    const include = JSON.stringify([
      {
        association: "branches",
        required: false,
        where: createWhereConditionForBranchId(branchId, currentUser?.companyId),
      },
      {
        association: "userCompany",
        attributes: ["leader"],
      },
      {
        association: "availabilities",
        required: false,
        where: {
          status: AvailabilityStatus.IN_PROGRESS,
        },
      },
      {
        association: "certifications",
      },
    ]);
    const filters = {
      companyId: currentUser?.companyId,
      status: Status.ACTIVE,
    };

    return useQuery({
      queryKey: ["order-page-all-members", branchId, ...Object.values(filters)],
      queryFn: async () => {
        return MembersService.getAll({
          ...filters,
          include,
        }).then(({ data }) => data);
      },

      refetchOnWindowFocus: false,
      enabled: hasCompanyAndBranchIdEnabled,

      initialData() {
        return [];
      },
    });
  };

  return {
    getTaskTypes,
    getAllTeams,
    getAllLeaders,
    getAllStaffs,
    getAllFleets,
    getAllMembers,
    hasCompanyAndBranchIdEnabled,
  };
};
